/**
 * SIL-1617 - pure helpers + wire types for the import review page
 * (routes/import/$batchId.tsx). Wire types are hand-mirrored from
 * apps/api/src/services/import/review.ts (the review routes build ad hoc JSON,
 * not a shared DTO); keep them in sync.
 */

export type MappedField =
  | 'serial_number'
  | 'manufacturer'
  | 'model'
  | 'seller_sku_raw'
  | 'condition_band'
  | 'lifecycle_state'
  | 'data_center_code'
  | 'asset_tag'
  | 'quantity'
  | 'raw_attribute'
  | 'ignore';

export interface MappingColumn {
  source: string;
  field: MappedField;
  site: string | null;
  confidence?: 'high' | 'medium' | 'low';
}

export interface ReviewPayload {
  batchId: string;
  statusId: string;
  filename: string | null;
  locked: boolean;
  readyForReviewAt: string | null;
  llmReviewStatus: string | null;
  /** Set once when the assistant escalated the batch to the SiliconX team. */
  escalatedAt: string | null;
  needsMapping: boolean;
  mapping: {
    proposal: { isTemplate: boolean; columns: MappingColumn[]; unresolved: string[] };
    reusedFromBatchId: string | null;
    sampleRows: string[][];
    confirmed: MappingColumn[] | null;
  } | null;
  counts: { total: number; accepted: number; rejected: number; needsReview: number; excluded: number };
  errorsByCode: Record<string, number>;
  openProposalCount: number;
}

export interface ProposalChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface Proposal {
  id: string;
  rowNum: number | null;
  changes: ProposalChange[];
  ruleCode: string;
  source: string;
  reason: string | null;
  groupKey: string | null;
  status: string;
}

export interface ProposalGroup {
  groupKey: string;
  source: string;
  ruleCode: string;
  count: number;
  reason: string;
}

export interface RowError {
  field: string;
  code: string;
  message: string;
}

export interface ReviewRow {
  id: string;
  rowNum: number | null;
  manufacturer: string | null;
  model: string | null;
  sellerSku: string | null;
  serialNumber: string | null;
  conditionBand: string | null;
  assetTag: string | null;
  quantity: number;
  site: string | null;
  parentRowNum: number | null;
  isValid: boolean;
  excluded: boolean;
  rowErrors: RowError[];
}

export type EditableField =
  | 'serial_number'
  | 'seller_sku_raw'
  | 'condition_band'
  | 'asset_tag'
  | 'site'
  | 'quantity'
  | 'exclude_row'
  | 'parent_row_num';

// ---------------------------------------------------------------------------
// Mapping step
// ---------------------------------------------------------------------------

export const FIELD_OPTIONS: { value: MappedField; label: string }[] = [
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'model', label: 'Model / description' },
  { value: 'seller_sku_raw', label: 'Part number' },
  { value: 'serial_number', label: 'Serial number' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'condition_band', label: 'Condition' },
  { value: 'lifecycle_state', label: 'Lifecycle state' },
  { value: 'data_center_code', label: 'Data center code' },
  { value: 'asset_tag', label: 'Asset tag' },
  { value: 'raw_attribute', label: 'Keep as extra attribute' },
  { value: 'ignore', label: 'Ignore this column' },
];

export const fieldLabel = (f: string): string => FIELD_OPTIONS.find((o) => o.value === f)?.label ?? f;

/** Fields that may be claimed by one column only (mirrors the API's bad_mapping check). */
const SINGLE_USE = (f: MappedField) => f !== 'raw_attribute' && f !== 'ignore';

/** Returns the fields claimed by more than one column; empty = mapping can be confirmed. */
export function duplicateFields(columns: readonly MappingColumn[]): MappedField[] {
  const seen = new Set<MappedField>();
  const dup = new Set<MappedField>();
  for (const c of columns) {
    if (!SINGLE_USE(c.field)) continue;
    if (seen.has(c.field)) dup.add(c.field);
    seen.add(c.field);
  }
  return [...dup];
}

/** Up to `n` distinct non-empty sample values of column `idx`. */
export function sampleValues(sampleRows: readonly string[][], idx: number, n = 3): string[] {
  const out: string[] = [];
  for (const r of sampleRows) {
    const v = (r[idx] ?? '').trim();
    if (v && !out.includes(v)) out.push(v);
    if (out.length >= n) break;
  }
  return out;
}

/** Body for POST mapping/confirm: only a quantity column carries a site. */
export function confirmPayload(columns: readonly MappingColumn[]) {
  return {
    columns: columns.map((c) => ({
      source: c.source,
      field: c.field,
      site: c.field === 'quantity' && c.site ? c.site.trim().toUpperCase() : null,
    })),
  };
}

// ---------------------------------------------------------------------------
// Status header
// ---------------------------------------------------------------------------

export type StatusTone = 'green' | 'amber' | 'red';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * One status line for the header. 0 accepted is ALWAYS red ("Not imported
 * yet"), never amber (spec 6.1) - the Techills failure was a quiet warning.
 */
export function statusLine(r: Pick<ReviewPayload, 'counts' | 'locked' | 'readyForReviewAt' | 'openProposalCount'>): {
  tone: StatusTone;
  text: string;
} {
  const { accepted, rejected } = r.counts;
  if (r.readyForReviewAt) return { tone: 'green', text: `Submitted for review - ${plural(accepted, 'line')} accepted` };
  if (accepted === 0) {
    return {
      tone: 'red',
      text:
        rejected > 0
          ? `Not imported yet - ${plural(rejected, 'row')} need${rejected === 1 ? 's' : ''} fixes`
          : 'Not imported yet - no importable rows',
    };
  }
  if (rejected > 0) return { tone: 'amber', text: `${accepted} ready, ${plural(rejected, 'row')} still need${rejected === 1 ? 's' : ''} fixes` };
  if (r.openProposalCount > 0) {
    return { tone: 'green', text: `${plural(accepted, 'line')} ready - ${plural(r.openProposalCount, 'suggestion')} left to review` };
  }
  return { tone: 'green', text: `${plural(accepted, 'line')} ready to submit` };
}

// ---------------------------------------------------------------------------
// Fix cards
// ---------------------------------------------------------------------------

const RULE_TITLE: Record<string, (n: number) => string> = {
  condition_new_synonym: (n) => `"New" condition on ${plural(n, 'row')}`,
  column_shift: () => 'Columns look shifted',
  serial_looks_like_part_number: (n) => `Serial looks like a part number on ${plural(n, 'row')}`,
  asset_tag_repeated: (n) => `Same asset tag repeated on ${plural(n, 'row')}`,
  child_license_row: (n) => `${plural(n, 'license row')} attach to a product above`,
  non_hardware_row: (n) => `${plural(n, 'row')} are not hardware`,
  zero_quantity: (n) => `${plural(n, 'row')} with zero stock`,
  agent_chat: (n) => `${plural(n, 'fix')} suggested in chat`,
};

export const groupTitle = (g: Pick<ProposalGroup, 'ruleCode' | 'count'>): string =>
  (RULE_TITLE[g.ruleCode] ?? ((n: number) => `${plural(n, 'suggestion')} (${g.ruleCode})`))(g.count);

export const sourceLabel = (source: string): string => (source === 'rule' ? 'Rule' : source === 'user' ? 'You' : 'AI');

/** "serial_number: ABC -> (empty)" lines for a proposal, before to after. */
export function describeChange(c: ProposalChange): string {
  const v = (x: string | null) => (x === null || x === '' ? '(empty)' : x);
  if (c.field === 'exclude_row') return c.newValue === 'true' ? 'Exclude this row' : 'Include this row';
  if (c.field === 'column_mapping') return 'Re-map the columns';
  return `${fieldLabel(c.field)}: ${v(c.oldValue)} -> ${v(c.newValue)}`;
}

/** rowNum -> open proposals on that row. */
export function proposalsByRow(proposals: readonly Proposal[]): Map<number, Proposal[]> {
  const m = new Map<number, Proposal[]>();
  for (const p of proposals) {
    if (p.rowNum === null) continue;
    const list = m.get(p.rowNum) ?? [];
    list.push(p);
    m.set(p.rowNum, list);
  }
  return m;
}
